import {
  keepPreviousData,
  queryOptions,
  useMutation,
  usePrefetchQuery,
  useQuery,
  useQueryClient,
  useSuspenseQueries,
  useSuspenseQuery,
  type QueryClient,
} from '@tanstack/react-query';
import { hostQueryKeys, Hosts } from '#api';
import type { GetHostsFilter } from '#api/hosts.ts';
import type { ResourceListOptions, Successful } from '#dn-types/api.ts';
import type { Host } from '#dn-types/host.ts';
import { usePrevious } from '#hooks/usePrevious.ts';
import { makeMaxResourceQueryFn } from '#util/api.ts';

/**
 * {@link getHostInitialData} scans host list caches for initial detail data.
 */
export function getHostInitialData(queryClient: QueryClient, hostId: string) {
  const queryKey = hostQueryKeys.lists();
  return {
    initialData: () => {
      const hostQueryResults = queryClient.getQueriesData<Successful<Host[] | null> | undefined>({ queryKey });
      const host = hostQueryResults
        // Count queries live under the same lists() prefix but hold a bare number, not a Host[].
        .flatMap((hit) => {
          const data = hit[1]?.data;
          return Array.isArray(data) ? data : [];
        })
        .find((d) => d.id === hostId);

      // If we found a match, use it as the initial data for the query
      if (host) {
        return { data: host };
      }
    },
    initialDataUpdatedAt: () =>
      queryClient.getQueryState<Successful<Host[] | null> | undefined>(queryKey)?.dataUpdatedAt,
  };
}

export const makeHostQueryOptions = (hostId: string) =>
  queryOptions({
    queryKey: hostQueryKeys.detail(hostId),
    queryFn: ({ signal }) => Hosts.getHost(hostId, { signal }),
  });

/**
 * {@link useSuspenseHostQuery} fetches host details, using cached results from list queries if possible.
 */
export function useSuspenseHostQuery(hostId: string) {
  const queryClient = useQueryClient();

  const { initialData, initialDataUpdatedAt } = getHostInitialData(queryClient, hostId);

  return useSuspenseQuery({
    ...makeHostQueryOptions(hostId),
    initialData,
    initialDataUpdatedAt,
  });
}

export const makeHostEffectiveAccessQueryOptions = (hostId: string) =>
  queryOptions({
    queryKey: [...hostQueryKeys.detail(hostId), 'effective-access'] as const,
    queryFn: ({ signal }) => Hosts.getHostEffectiveAccess(hostId, { signal }),
  });

/**
 * {@link useHostEffectiveAccessQuery} lists what a host may reach. Deliberately non-suspense: the
 * card is additive to the host detail page, so it resolves on its own without holding up the page.
 */
export function useHostEffectiveAccessQuery(hostId: string) {
  return useQuery(makeHostEffectiveAccessQueryOptions(hostId));
}

// An empty filter and an absent filter are the same request; normalize so they share a cache entry.
const normalizeFilter = (filter?: GetHostsFilter) => (filter && Object.keys(filter).length > 0 ? filter : undefined);

export const makeHostsQueryOptions = (cursor?: string, pageSize?: number, filter?: GetHostsFilter) =>
  queryOptions({
    queryKey: hostQueryKeys.list(cursor, pageSize, normalizeFilter(filter)),
    queryFn: ({ signal }) => Hosts.getHosts({ cursor, pageSize, filter, init: { signal } }),
  });

export const makeMaxHostsQueryOptions = (filter?: GetHostsFilter) =>
  queryOptions({
    // The filter must be part of the key or differently-filtered max-lists would share a cache entry.
    queryKey: hostQueryKeys.list(undefined, Number.POSITIVE_INFINITY, normalizeFilter(filter)),
    queryFn: makeMaxResourceQueryFn((options: ResourceListOptions) =>
      Hosts.getHosts({ cursor: options.cursor, pageSize: options.pageSize, init: options.init, filter })
    ),
  });

export function useHostsQuery(cursor?: string, pageSize?: number, filter?: GetHostsFilter) {
  const queryClient = useQueryClient();
  const hostsQuery = useQuery({
    ...makeHostsQueryOptions(cursor, pageSize, filter),
    placeholderData: keepPreviousData,
  });

  const { isSuccess, data: newResponse, dataUpdatedAt } = hostsQuery;
  const previousResponse = usePrevious(newResponse);
  const previousDataUpdatedAt = usePrevious(dataUpdatedAt);

  if (isSuccess) {
    if (
      newResponse.metadata.hasPrevPage &&
      previousResponse?.metadata.page &&
      newResponse.metadata.page?.start === previousResponse.metadata.page.start + previousResponse.metadata.page.count
    ) {
      queryClient.setQueryData(
        makeHostsQueryOptions(newResponse.metadata.prevCursor, pageSize, filter).queryKey,
        {
          data: previousResponse.data,
          metadata: previousResponse.metadata,
        },
        { updatedAt: previousDataUpdatedAt }
      );
    }
  }

  return hostsQuery;
}

export function useMaxHostsQuery(filter?: GetHostsFilter) {
  return useQuery(makeMaxHostsQueryOptions(filter));
}

/**
 * {@link hostsCountQueryOptions} counts hosts matching `filter` — e.g. a single network via
 * `{ networkID }`, lighthouses via {@link LIGHTHOUSE_FILTER}, or the whole account with no filter.
 * pageSize:1 — we only read metadata.totalCount.
 * Keyed under `lists()` so list invalidations also refresh the count.
 */
export const hostsCountQueryOptions = (filter: GetHostsFilter = {}) =>
  queryOptions({
    queryKey: [...hostQueryKeys.lists(), 'count', filter] as const,
    // TODO: metadata is always defined on this query, the types should reflect that.
    queryFn: async ({ signal }) =>
      (await Hosts.getHosts({ filter, pageSize: 1, init: { signal } })).metadata.totalCount,
  });

/**
 * {@link useSuspenseHostsCount} returns the total number of hosts matching `filter` for the current account.
 */
export function useSuspenseHostsCount(filter?: GetHostsFilter) {
  return useSuspenseQuery(hostsCountQueryOptions(filter));
}

export function usePrefetchHostsCount() {
  usePrefetchQuery(hostsCountQueryOptions());
}

// Creates multiple concurrent suspense requests for host details.
export function useSuspenseHostsDetailsQueries(hostIds: string[]) {
  return useSuspenseQueries({
    queries: hostIds.map((hostId) => ({
      ...makeHostQueryOptions(hostId),
    })),
  });
}

/**
 * {@link useBlockHostMutation} blocks a host via POST /v2/hosts/:id/block and invalidates host caches.
 */
export function useBlockHostMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (hostId: string) => Hosts.blockHost(hostId),
    onSuccess: async (data, hostId) => {
      await Promise.all([
        // Invalidate list caches (stale after block state change)
        queryClient.invalidateQueries({ queryKey: hostQueryKeys.lists() }),
        // Optimistically set detail cache
        queryClient.setQueryData(makeHostQueryOptions(hostId).queryKey, { data }),
      ]);
    },
  });
}

/**
 * {@link useAddHostIP} adds an IP address to a host via POST /v2/hosts/:id/ip-addresses and invalidates host caches.
 */
export function useAddHostIP() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: Hosts.AddHostIPVariables) => Hosts.addHostIP(variables),
    onSuccess: async (data, { hostId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: hostQueryKeys.lists() }),
        queryClient.setQueryData(makeHostQueryOptions(hostId).queryKey, { data }),
      ]);
    },
  });
}

/**
 * {@link useUnblockHostMutation} unblocks a host via POST /v2/hosts/:id/unblock and invalidates host caches.
 */
export function useUnblockHostMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (hostId: string) => Hosts.unblockHost(hostId),
    onSuccess: async (data, hostId) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: hostQueryKeys.lists() }),
        queryClient.setQueryData(makeHostQueryOptions(hostId).queryKey, { data }),
      ]);
    },
  });
}
